import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Card, Dialog, Field, Input, Select, Switch, Textarea, cn, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Pencil, Plus, Trash2, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PartSupplierClient, refusalMessage, SupplierClient, type PartGridDTO, type PartSupplierDTO } from "../../api/manufacturing";
import {
  fillLabel,
  OrderUnitClient,
  orderUnitName,
  ReplenishmentClient,
  replenishmentKeys,
  type OrderUnitDTO,
  type OrderUnitKind,
  type SharedUnitGauge,
} from "../../api/replenishment";
import { SupplierLink } from "../../components/SupplierLink";
import { NexpimView } from "../../views";
import { PartPicker } from "../manufacturing/PartPicker";
import { formatCad, formatQuantity } from "./replenishment/shared";

/**
 * Order units: what things are bought in, apart from how they are counted.
 *
 * <p>A PER_PART unit (a barrel, a roll, a box) holds an amount of one part, set
 * per part and supplier: a barrel of this white is 208 L. Orders are rounded
 * up to whole ones. A SHARED unit (a truck, a container) carries one
 * supplier's parts together, against a capacity in its own measure, at the
 * fill levels the supplier sells (a quarter, a half, a full truck). The
 * replenishment run plans a SHARED unit as one order: what is due, what is
 * close enough to join, filled to the level paid for and trimmed to fit.</p>
 *
 * <p>The parts are the part-supplier links that order in the unit: here, or
 * on a part's Purchases tab. A SHARED unit's parts must be bought from its
 * supplier; the server refuses the rest with a sentence, shown as it comes.</p>
 */
export function OrderUnitsPage() {
  const { t, i18n } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const allowed = hasView(NexpimView.PURCHASING);
  const units = useMemo(() => new OrderUnitClient(http), [http]);
  const replenishment = useMemo(() => new ReplenishmentClient(http), [http]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState<OrderUnitDTO | "new" | null>(null);

  const list = useQuery({
    queryKey: replenishmentKeys.orderUnits,
    queryFn: () => units.findAll(),
    enabled: allowed,
  });
  const summary = useQuery({
    queryKey: replenishmentKeys.summary,
    queryFn: () => replenishment.summary(),
    enabled: allowed,
    staleTime: 60_000,
  });

  const sorted = useMemo(
    () =>
      [...(list.data ?? [])].sort(
        (a, b) => Number(b.is_active) - Number(a.is_active) || a.kind.localeCompare(b.kind) || a.code.localeCompare(b.code),
      ),
    [list.data],
  );
  const selected = sorted.find((unit) => unit.id === selectedId) ?? sorted[0] ?? null;
  const gauge = (id: number) => summary.data?.shared_units.find((unit) => unit.order_unit_id === id);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.order-units", "Order units")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.order-units", "Order units")}
        subtitle={t("order-unit.subtitle", "What things are bought in: barrels, rolls and boxes of one part; trucks and containers shared by a supplier's parts.")}
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setEditing("new")}>
              <Plus className="size-4" />
              {t("order-unit.new", "New order unit")}
            </Button>
          ) : null
        }
      />
      <div className="grid gap-4 p-4 lg:grid-cols-[20rem_1fr] lg:p-6">
        <div className="flex flex-col gap-2">
          {list.isLoading && (
            <div className="flex h-24 items-center justify-center">
              <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            </div>
          )}
          {!list.isLoading && sorted.length === 0 && (
            <Card className="p-4 text-[13px] text-ink-muted">
              <p className="font-medium text-ink">{t("order-unit.empty", "No order units yet")}</p>
              <p className="mt-1">
                {t(
                  "order-unit.empty.detail",
                  "Create one for each way things are bought: a barrel of paint, a roll of paper, a truck of panels. Then set, on each part, how much one holds.",
                )}
              </p>
            </Card>
          )}
          {sorted.map((unit) => (
            <button
              key={unit.id}
              type="button"
              onClick={() => setSelectedId(unit.id)}
              className={cn(
                "rounded-lg border bg-surface p-3 text-left shadow-card transition",
                selected?.id === unit.id ? "border-accent ring-1 ring-accent" : "border-line hover:border-ink-faint",
                !unit.is_active && "opacity-60",
              )}
            >
              <div className="flex items-center gap-2">
                {unit.kind === "SHARED" ? <Truck className="size-4 text-ink-muted" /> : <Package className="size-4 text-ink-muted" />}
                <span className="font-semibold text-ink">{unit.code}</span>
                <span className="min-w-0 flex-1 truncate text-[12px] text-ink-muted">{orderUnitName(unit, i18n.language)}</span>
                {!unit.is_active && (
                  <Badge variant="neutral" size="sm">
                    {t("status.inactive", "Inactive")}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-[12px] text-ink-muted">
                {unit.kind === "SHARED" ? `${unit.supplier_name ?? ""} · ` : `${kindLabel(t, unit.kind)} · `}
                {t("order-unit.parts-count", "{{count}} parts", { count: unit.part_count ?? 0 })}
              </p>
              {unit.kind === "SHARED" && <LoadGauge unit={unit} gauge={gauge(unit.id)} className="mt-2" />}
            </button>
          ))}
        </div>
        {selected ? (
          <UnitDetail
            key={selected.id}
            unit={selected}
            gauge={gauge(selected.id)}
            canWrite={canWrite}
            onEdit={() => setEditing(selected)}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <div />
        )}
      </div>
      {editing && (
        <UnitDialog
          unit={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setSelectedId(saved.id);
          }}
        />
      )}
    </>
  );
}

function kindLabel(t: (key: string, fallback: string) => string, kind: OrderUnitKind): string {
  return kind === "SHARED" ? t("order-unit.kind.shared", "Shared: a truck, a container") : t("order-unit.kind.per-part", "Per part: a barrel, a roll, a box");
}

/**
 * A shared unit as a bar: what the latest run loads in it against its
 * capacity, with ticks at its fill levels (or at the minimum load without
 * them), and the level it is billed at.
 */
function LoadGauge({ unit, gauge, className }: { unit: OrderUnitDTO; gauge: SharedUnitGauge | undefined; className?: string }) {
  const { t } = useTranslation();
  const load = gauge?.planned_load ?? 0;
  const capacity = Math.max(unit.capacity ?? 0, 1);
  const pct = Math.min(100, (load / capacity) * 100);
  const ticks = unit.fill_levels.length > 0 ? unit.fill_levels : [((unit.minimum_load ?? 0) / capacity)];
  // With fill levels any load is billed at a level: only a minimum load can leave it short.
  const lowest = unit.fill_levels.length > 0 ? 0 : (unit.minimum_load ?? 0);
  const tone = !gauge?.is_due ? "bg-ink-faint" : gauge.is_urgent ? "bg-risk" : load >= lowest ? "bg-ok" : "bg-warn";
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="relative h-2 rounded-full bg-surface-sunk">
        <div className={cn("absolute inset-y-0 left-0 rounded-full", tone)} style={{ width: `${String(pct)}%` }} />
        {ticks
          .filter((tick) => tick > 0 && tick < 1)
          .map((tick) => (
            <div
              key={tick}
              className="absolute -inset-y-0.5 w-0.5 bg-ink-muted"
              style={{ left: `${String(tick * 100)}%` }}
              title={unit.fill_levels.length > 0 ? fillLabel(tick, t) : t("order-unit.minimum-load", "Minimum load")}
            />
          ))}
      </div>
      <div className="flex justify-between gap-2 text-[11px] tabular-nums text-ink-faint">
        <span>
          {formatQuantity(load)} / {formatQuantity(unit.capacity ?? 0)} {unit.load_unit_label}
          {gauge?.planned_fill != null && unit.fill_levels.length > 0 && ` · ${t("order-unit.billed-at", "billed {{fill}}", { fill: fillLabel(gauge.planned_fill, t) })}`}
        </span>
        <span>
          {!gauge?.is_due
            ? t("order-unit.not-due", "nothing due")
            : gauge.is_urgent
              ? t("order-unit.urgent", "urgent: a part is in red")
              : t("order-unit.due", "due · {{amount}}", { amount: formatCad(gauge.order_amount_cad) })}
        </span>
      </div>
    </div>
  );
}

/** The links that order in a unit, with what each says about it. */
function UnitDetail({
  unit,
  gauge,
  canWrite,
  onEdit,
  onDeleted,
}: {
  unit: OrderUnitDTO;
  gauge: SharedUnitGauge | undefined;
  canWrite: boolean;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const units = useMemo(() => new OrderUnitClient(http), [http]);
  const links = useMemo(() => new PartSupplierClient(http), [http]);
  const linksKey = ["replenishment", "order-unit-links", unit.id] as const;
  const shared = unit.kind === "SHARED";

  const rows = useQuery({
    queryKey: linksKey,
    queryFn: async () => (await links.find({ page: 0, size: 1000, filters: { order_unit_id: [String(unit.id)] } })).content,
  });

  const [picked, setPicked] = useState<PartGridDTO | null>(null);
  const [amount, setAmount] = useState("1");

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: linksKey });
    await queryClient.invalidateQueries({ queryKey: replenishmentKeys.orderUnits });
  };

  /** Writes a link's order unit figures, keeping everything else on it as it is. */
  const withUnit = (link: PartSupplierDTO, figure: number | null, minimum: number | null): PartSupplierDTO => ({
    ...link,
    order_unit_id: figure == null ? null : unit.id,
    stock_units_per_order_unit: figure != null && !shared ? figure : null,
    load_per_stock_unit: figure != null && shared ? figure : null,
    minimum_order_units: figure != null && !shared ? minimum : null,
  });

  const add = useMutation({
    mutationFn: async () => {
      // A shared unit takes the part's link with its supplier; a per-part one the part's first supplier.
      const found = (
        await links.find({
          page: 0,
          size: 20,
          filters: shared ? { part_id: [String(picked?.id ?? 0)], supplier_id: [String(unit.supplier_id ?? 0)] } : { part_id: [String(picked?.id ?? 0)] },
          sort: ["priority,asc"],
        })
      ).content;
      const link = found[0];
      if (!link) {
        throw new Error(
          shared
            ? t("order-unit.not-bought-from", "{{part}} is not bought from {{supplier}}: add the supplier on the part first.", {
                part: picked?.number ?? "",
                supplier: unit.supplier_name ?? "",
              })
            : t("order-unit.no-supplier", "{{part}} has no supplier yet: add one on the part first.", { part: picked?.number ?? "" }),
        );
      }
      await links.update(withUnit(link, Number(amount), null));
    },
    onSuccess: async () => {
      setPicked(null);
      setAmount("1");
      await refresh();
      toast.success(t("order-unit.part-added", "The part is ordered in {{code}}", { code: unit.code }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, failure instanceof Error ? failure.message : t("error.generic", "Something went wrong."))),
  });

  const remove = useMutation({
    mutationFn: () => units.remove(unit.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: replenishmentKeys.orderUnits });
      onDeleted();
      toast.success(t("order-unit.deleted", "Order unit deleted"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const figureHeader = shared
    ? t("order-unit.load-per-stock-unit", "Load per stock unit ({{unit}})", { unit: unit.load_unit_label ?? "" })
    : t("order-unit.stock-units-per", "Stock units in one {{code}}", { code: unit.code });
  const cols = useMemo(() => {
    const c = defineColumns<PartSupplierDTO>();
    return [
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name ?? "", { headerKey: "column.part.name", priority: 2 }),
      ...(shared ? [] : [c.text("supplier_name", (row) => row.supplier_name ?? "", { headerKey: "column.supplier", priority: 2 })]),
      c.number("figure", (row) => (shared ? row.load_per_stock_unit : row.stock_units_per_order_unit) ?? 0, {
        header: figureHeader,
        filter: "none",
        priority: 1,
      }),
      ...(shared
        ? []
        : [
            c.number("minimum_order_units", (row) => row.minimum_order_units ?? 0, {
              header: t("order-unit.minimum-units", "Fewest per order"),
              filter: "none",
              priority: 2,
            }),
          ]),
    ];
  }, [t, shared, figureHeader]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-ink">
              {unit.code} <span className="font-normal text-ink-muted">· {orderUnitName(unit, i18n.language)}</span>
            </h2>
            <p className="text-[13px] text-ink-muted">
              {kindLabel(t, unit.kind)}
              {shared && unit.supplier_id != null && (
                <>
                  {" · "}
                  <SupplierLink id={unit.supplier_id}>{unit.supplier_name}</SupplierLink>
                </>
              )}
            </p>
          </div>
          {canWrite && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={onEdit}>
                <Pencil className="size-3.5" />
                {t("button.edit", "Edit")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("order-unit.delete", "Delete order unit")}
                title={t("order-unit.delete", "Delete order unit")}
                loading={remove.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: t("order-unit.delete.title", "Delete order unit {{code}}?", { code: unit.code }),
                    message: t(
                      "order-unit.delete.message",
                      "Only a unit no part is ordered in can go: take its parts off first, or mark it inactive. Past runs keep their record.",
                    ),
                    variant: "danger",
                    confirmLabel: t("button.delete", "Delete"),
                  });
                  if (ok) remove.mutate();
                }}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          )}
        </div>
        {shared && (
          <>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-[13px] sm:grid-cols-4">
              <Stat label={t("order-unit.capacity", "Capacity")} value={`${formatQuantity(unit.capacity ?? 0)} ${unit.load_unit_label ?? ""}`} />
              <Stat
                label={unit.fill_levels.length > 0 ? t("order-unit.fill-levels", "Fill levels") : t("order-unit.minimum-load", "Minimum load")}
                value={
                  unit.fill_levels.length > 0
                    ? unit.fill_levels.map((level) => fillLabel(level, t)).join(", ")
                    : `${formatQuantity(unit.minimum_load ?? 0)} ${unit.load_unit_label ?? ""}`
                }
              />
              <Stat label={t("order-unit.cycle", "Every")} value={t("order-unit.cycle-days", "{{count}} days", { count: unit.order_cycle_days ?? 0 })} />
              <Stat label={t("order-unit.parts", "Parts")} value={String(rows.data?.length ?? unit.part_count ?? 0)} />
            </dl>
            <div className="mt-4">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-ink-faint">{t("order-unit.latest-run", "Latest run")}</p>
              <LoadGauge unit={unit} gauge={gauge} />
            </div>
          </>
        )}
        {unit.note && <p className="mt-3 text-[12px] text-ink-muted">{unit.note}</p>}
      </Card>

      {canWrite && (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-64 flex-1">
            <p className="mb-1 text-[12px] font-medium text-ink">{t("order-unit.add-part", "Order a part in it")}</p>
            {picked ? (
              <div className="flex h-9 items-center gap-2 rounded-md border border-line bg-surface-sunk px-3 text-[13px]">
                <span className="font-medium">{picked.number}</span>
                <span className="min-w-0 flex-1 truncate text-ink-muted">{picked.name}</span>
                <button type="button" className="text-[12px] text-accent-strong" onClick={() => setPicked(null)}>
                  {t("button.change", "Change")}
                </button>
              </div>
            ) : (
              <PartPicker
                onPick={setPicked}
                exclude={(rows.data ?? []).map((row) => row.part_id)}
                placeholder={
                  shared
                    ? t("order-unit.find-part-shared", "Find a part bought from {{supplier}}…", { supplier: unit.supplier_name ?? "" })
                    : t("order-unit.find-part", "Find a part…")
                }
              />
            )}
          </div>
          <Field label={figureHeader} htmlFor="order-unit-figure" className="w-52">
            <Input id="order-unit-figure" type="number" min={0} step="any" value={amount} onChange={(event) => setAmount(event.target.value)} />
          </Field>
          <Button variant="primary" disabled={!picked || !(Number(amount) > 0)} loading={add.isPending} onClick={() => add.mutate()}>
            <Plus className="size-4" />
            {t("button.add", "Add")}
          </Button>
        </Card>
      )}

      <DataTable<PartSupplierDTO>
        data={rows.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={rows.isLoading}
        editor={
          canWrite
            ? {
                fields: {
                  figure: {
                    kind: "number",
                    get: (row) => (shared ? row.load_per_stock_unit : row.stock_units_per_order_unit) ?? 0,
                    set: (row, value) =>
                      shared ? { ...row, load_per_stock_unit: Number(value) } : { ...row, stock_units_per_order_unit: Number(value) },
                    required: true,
                  },
                  ...(shared
                    ? {}
                    : {
                        minimum_order_units: {
                          kind: "number" as const,
                          get: (row: PartSupplierDTO) => row.minimum_order_units ?? 0,
                          set: (row: PartSupplierDTO, value: unknown) => ({
                            ...row,
                            minimum_order_units: Number(value) > 0 ? Math.round(Number(value)) : null,
                          }),
                        },
                      }),
                },
                validate: (row) =>
                  ((shared ? row.load_per_stock_unit : row.stock_units_per_order_unit) ?? 0) > 0
                    ? null
                    : { figure: t("order-unit.figure-positive", "Must be above zero.") },
                onSave: async (row) => {
                  try {
                    await links.update(row);
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                  await refresh();
                },
                onDelete: async (row) => {
                  await links.update(withUnit(row, null, null));
                  await refresh();
                },
                deleteConfirm: (row) => ({
                  title: t("order-unit.remove.title", "Stop ordering {{part}} in {{code}}?", { part: row.part_number ?? "", code: unit.code }),
                  message: shared
                    ? t("order-unit.remove.message.shared", "It will be planned on its own from the next run.")
                    : t("order-unit.remove.message", "It will be ordered in its stock unit from the next run."),
                }),
              }
            : undefined
        }
        emptyMessage={t("order-unit.no-parts", "No part is ordered in it yet.")}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}

/** "0.25, 0.5, 1" or "1/4, 1/2, full" → fractions, ascending; null when something is not one. */
function parseLevels(text: string): number[] | null {
  const parts = text.split(/[,;\s]+/).filter(Boolean);
  const levels: number[] = [];
  for (const part of parts) {
    const fraction = /^(\d+)\/(\d+)$/.exec(part);
    const value = fraction ? Number(fraction[1]) / Number(fraction[2]) : Number(part.replace(",", "."));
    if (!(value > 0 && value <= 1)) return null;
    levels.push(value);
  }
  return [...new Set(levels)].sort((a, b) => a - b);
}

function UnitDialog({
  unit,
  onClose,
  onSaved,
}: {
  unit: OrderUnitDTO | null;
  onClose: () => void;
  onSaved: (unit: OrderUnitDTO) => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new OrderUnitClient(http), [http]);
  const [draft, setDraft] = useState<OrderUnitDTO>(
    () =>
      unit ?? {
        id: 0,
        code: "",
        name: "",
        name_fr: "",
        kind: "PER_PART",
        supplier_id: null,
        capacity: null,
        minimum_load: null,
        load_unit_label: null,
        fill_levels: [],
        order_cycle_days: null,
        is_active: true,
        note: null,
      },
  );
  const set = <K extends keyof OrderUnitDTO>(key: K, value: OrderUnitDTO[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const shared = draft.kind === "SHARED";
  const inUse = (unit?.part_count ?? 0) > 0;
  // The numbers are typed as text: a number state turns a cleared field into 0
  // and then greys Save without saying why.
  const [numbers, setNumbers] = useState(() => ({
    capacity: draft.capacity == null ? "" : String(draft.capacity),
    minimum_load: draft.minimum_load == null ? "0" : String(draft.minimum_load),
    order_cycle_days: draft.order_cycle_days == null ? "7" : String(draft.order_cycle_days),
    fill_levels: draft.fill_levels.map(String).join(", "),
  }));
  const setNumber = (key: keyof typeof numbers, value: string) => setNumbers((current) => ({ ...current, [key]: value }));
  const parsed = (value: string) => (value.trim() === "" ? Number.NaN : Number(value));
  const capacity = parsed(numbers.capacity);
  const minimumLoad = parsed(numbers.minimum_load);
  const cycleDays = parsed(numbers.order_cycle_days);
  const levels = numbers.fill_levels.trim() === "" ? [] : parseLevels(numbers.fill_levels);
  const capacityError = !shared || capacity > 0 ? undefined : t("order-unit.capacity.error", "Must be above zero.");
  const minimumError = !shared
    ? undefined
    : !(minimumLoad >= 0)
      ? t("order-unit.minimum-load.invalid", "Zero or more.")
      : capacity > 0 && minimumLoad > capacity
        ? t("order-unit.minimum-load.error", "Cannot exceed the capacity.")
        : undefined;
  const cycleError = !shared || (Number.isInteger(cycleDays) && cycleDays >= 1) ? undefined : t("order-unit.cycle.error", "A whole number of days, 1 or more.");
  const levelsError = !shared || levels != null ? undefined : t("order-unit.fill-levels.error", "Fractions above 0 and at most 1: 0.25, 0.5, 1 or 1/4, 1/2, 1.");

  const suppliers = useQuery({
    queryKey: ["supplier", "all"],
    queryFn: () => new SupplierClient(http).findAll(),
    staleTime: 5 * 60_000,
    enabled: shared,
  });

  const save = useMutation({
    mutationFn: async () => {
      const row: OrderUnitDTO = shared
        ? {
            ...draft,
            code: draft.code.trim(),
            name: draft.name.trim(),
            name_fr: draft.name_fr.trim(),
            capacity,
            minimum_load: minimumLoad,
            order_cycle_days: cycleDays,
            fill_levels: levels ?? [],
            load_unit_label: draft.load_unit_label?.trim() || t("order-unit.default-load-unit", "sheets"),
          }
        : {
            ...draft,
            code: draft.code.trim(),
            name: draft.name.trim(),
            name_fr: draft.name_fr.trim(),
            supplier_id: null,
            capacity: null,
            minimum_load: null,
            order_cycle_days: null,
            fill_levels: [],
            load_unit_label: null,
          };
      if (unit) {
        await client.update(row);
        return row;
      }
      return client.create(row);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: replenishmentKeys.orderUnits });
      toast.success(t("notification.saved", "Saved"));
      onSaved(saved);
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const valid =
    draft.code.trim() !== "" &&
    draft.name.trim() !== "" &&
    (!shared || (draft.supplier_id ?? 0) > 0) &&
    !capacityError &&
    !minimumError &&
    !cycleError &&
    !levelsError;

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={unit ? t("order-unit.edit", "Edit order unit") : t("order-unit.new", "New order unit")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label={t("order-unit.kind", "Kind")}
          htmlFor="order-unit-kind"
          className="sm:col-span-2"
          hint={inUse ? t("order-unit.kind.locked", "Parts are ordered in it: its kind and supplier stay as they are.") : undefined}
        >
          <Select
            id="order-unit-kind"
            value={draft.kind}
            disabled={inUse}
            options={[
              { value: "PER_PART", label: kindLabel(t, "PER_PART") },
              { value: "SHARED", label: kindLabel(t, "SHARED") },
            ]}
            onChange={(event) => set("kind", event.target.value as OrderUnitKind)}
          />
        </Field>
        <Field label={t("order-unit.code", "Code")} htmlFor="order-unit-code" required>
          <Input id="order-unit-code" value={draft.code} maxLength={20} onChange={(event) => set("code", event.target.value.toUpperCase())} />
        </Field>
        <div />
        <Field label={t("order-unit.name", "Name")} htmlFor="order-unit-name" required>
          <Input id="order-unit-name" value={draft.name} onChange={(event) => set("name", event.target.value)} />
        </Field>
        <Field label={t("order-unit.name-fr", "Name in French")} htmlFor="order-unit-name-fr" hint={t("order-unit.name-fr.hint", "Empty: the English name.")}>
          <Input id="order-unit-name-fr" value={draft.name_fr} onChange={(event) => set("name_fr", event.target.value)} />
        </Field>
        {shared && (
          <>
            <Field label={t("order-unit.supplier", "Supplier")} htmlFor="order-unit-supplier" required className="sm:col-span-2">
              <Select
                id="order-unit-supplier"
                value={draft.supplier_id ? String(draft.supplier_id) : ""}
                disabled={inUse}
                placeholder={t("form.choose", "Choose…")}
                options={(suppliers.data ?? [])
                  .filter((supplier) => supplier.is_active || supplier.id === draft.supplier_id)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((supplier) => ({ value: String(supplier.id), label: `${supplier.name} (${supplier.code})` }))}
                onChange={(event) => set("supplier_id", Number(event.target.value))}
              />
            </Field>
            <Field label={t("order-unit.capacity", "Capacity")} htmlFor="order-unit-capacity" required error={capacityError}>
              <Input id="order-unit-capacity" type="number" min={0} step="any" value={numbers.capacity} onChange={(event) => setNumber("capacity", event.target.value)} />
            </Field>
            <Field
              label={t("order-unit.load-unit", "Load unit")}
              htmlFor="order-unit-load-unit"
              hint={t("order-unit.load-unit.hint", "What capacity is counted in: sheets, pallets, m3.")}
            >
              <Input
                id="order-unit-load-unit"
                value={draft.load_unit_label ?? ""}
                onChange={(event) => set("load_unit_label", event.target.value === "" ? null : event.target.value)}
              />
            </Field>
            <Field
              label={t("order-unit.fill-levels", "Fill levels")}
              htmlFor="order-unit-levels"
              hint={t("order-unit.fill-levels.hint", "What the supplier sells, as fractions of a full one: 1/4, 1/2, 1. A load is billed at the next level and filled up to it. Empty: any load from the minimum.")}
              error={levelsError}
            >
              <Input id="order-unit-levels" value={numbers.fill_levels} placeholder="1/4, 1/2, 1" onChange={(event) => setNumber("fill_levels", event.target.value)} />
            </Field>
            <Field
              label={t("order-unit.minimum-load", "Minimum load")}
              htmlFor="order-unit-minimum"
              hint={t("order-unit.minimum-load.hint", "Without fill levels: below this it waits, and parts close to their order point are added to reach it.")}
              error={minimumError}
            >
              <Input
                id="order-unit-minimum"
                type="number"
                min={0}
                step="any"
                value={numbers.minimum_load}
                onChange={(event) => setNumber("minimum_load", event.target.value)}
              />
            </Field>
            <Field
              label={t("order-unit.cycle-field", "Order cycle (days)")}
              htmlFor="order-unit-cycle"
              required
              hint={t("order-unit.cycle.hint", "How often one goes: its parts order this many days' worth.")}
              error={cycleError}
            >
              <Input id="order-unit-cycle" type="number" min={1} step={1} value={numbers.order_cycle_days} onChange={(event) => setNumber("order_cycle_days", event.target.value)} />
            </Field>
          </>
        )}
        <Field label={t("column.note", "Note")} htmlFor="order-unit-note" className="sm:col-span-2">
          <Textarea id="order-unit-note" rows={2} value={draft.note ?? ""} onChange={(event) => set("note", event.target.value === "" ? null : event.target.value)} />
        </Field>
        <Switch checked={draft.is_active} onChange={(value) => set("is_active", value)} label={t("order-unit.active", "Active: used by the run")} />
      </div>
    </Dialog>
  );
}
